import { useThemeContext } from '@space-core/hooks'
import BaseLayout from '@space-shell/common/components/baseLayout'
import ErrorBoundary from '@space-shell/common/components/errorBoundary'
import LoaderFull from '@space-shell/common/components/loaderFull'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'

import { useGoogleAnalyticsPageView } from '@space-shell/common/hooks/useGoogleAnalyticsPageView'
import useRequest from '@space-shell/common/hooks/useRequest'
import { MembershipService } from '@space-shell/common/services/membership'
import { UserEndpoints, UserService } from '@space-shell/common/services/user'
import { useAuthStore, useMembershipStore, useUserInfoStore } from '@space-shell/core/store'
import { lazyRemote } from '@space-shell/lib/runtime'

const UserProfileProvider = lazyRemote('community/userProfileContext', 'UserProfileProvider')

import { useParams, Outlet } from 'react-router-dom'

type RouteParams = {
  membership: string
  lang: string
}

const RouteGuard = () => {
  useGoogleAnalyticsPageView()

  const { membership, lang } = useParams<RouteParams>()
  const { token, ucode } = useAuthStore()
  const { authStatus } = useAuthContext()
  const { theme } = useThemeContext()

  const membershipSlug = String(membership)

  const isAuthenticated = authStatus === AuthStatus.AUTHENTICATED
  const isRenewing = authStatus === AuthStatus.RENEWING

  /**
   * Note: This request is necessary to invalidate membership data when navigating between courses
   * from different slugs within the user's profile, as soon as possible remove this duplicate, as
   * this request is already being made on the server-side
   */
  const membershipRequest = useRequest(
    ['membershipInfo', membershipSlug],
    () => MembershipService.getInfo(membershipSlug),
    {
      enabled: Boolean(membershipSlug),
      onSuccess: (membershipData) => {
        useMembershipStore.setState({ membershipData, membershipSlug: membershipData.slug })
      }
    }
  )

  useRequest([UserEndpoints.info()], () => UserService.getInfo(membershipSlug), {
    enabled: Boolean(membershipRequest.data) && isAuthenticated,
    onSuccess: (userInfo) => {
      useUserInfoStore.setState({ ...userInfo })
    }
  })

  if (isRenewing) {
    return <LoaderFull />
  }

  return (
    <ErrorBoundary>
      <UserProfileProvider
        key={ucode}
        token={token}
        loggedUserUcode={ucode}
        locale={lang}
        theme={theme}>
        <BaseLayout>
          <Outlet />
        </BaseLayout>
      </UserProfileProvider>
    </ErrorBoundary>
  )
}

export default RouteGuard
